import * as React from 'react';
import { Stack, Typography } from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { useFormikContext } from 'formik';
import { SxProps, Theme } from '@mui/material/styles';
import ClientFields from './ClientFields';
import {
  ClientSearchAutofillValues,
  getInputName,
  getValue,
} from '../../utils/form/clientValues';
import {
  ClientDisplayAppointment,
  getClientDisplay,
} from '../../helpers/customers';

const EDIT_FIELDS = ['firstName', 'lastName', 'email', 'phoneNumber', 'dob'];

const pillSx: SxProps<Theme> = {
  borderRadius: '50px',
  cursor: 'pointer',
  userSelect: 'none',
  lineHeight: 1,
  typography: 'h5',
  fontWeight: 700,
};

interface Props {
  namePrefix: string[];
  appointment?: ClientDisplayAppointment | null;
  disableCustomerEdit: boolean;
  hideBirthday: boolean;
  canRemove: boolean;
  onRemove: () => void;
}

export default function ClientCard({
  namePrefix,
  appointment,
  disableCustomerEdit,
  hideBirthday,
  canRemove,
  onRemove,
}: Props) {
  const { values, setFieldValue, validateForm } =
    useFormikContext<ClientSearchAutofillValues>();
  const [editing, setEditing] = React.useState(false);
  // Captured when editing opens so Cancel can put the values back — nothing is
  // persisted until the host form submits either way.
  const snapshot = React.useRef<Record<string, unknown>>({});

  const read = (name: string) => getValue(values, namePrefix, name);

  const { initials, name, meta } = getClientDisplay(
    {
      firstName: read('firstName'),
      lastName: read('lastName'),
      email: read('email'),
      phoneNumber: read('phoneNumber'),
    },
    appointment,
  );

  const openEdit = () => {
    snapshot.current = Object.fromEntries(
      EDIT_FIELDS.map(field => [field, read(field)]),
    );
    setEditing(true);
  };

  const cancelEdit = async () => {
    // Match onClearCustomer: skip per-field validation while batch-restoring,
    // then validate once at the end.
    await Promise.all(
      EDIT_FIELDS.map(field =>
        setFieldValue(
          getInputName(namePrefix, field),
          snapshot.current[field] ?? '',
          false,
        ),
      ),
    );
    setEditing(false);
    await validateForm();
  };

  return (
    <Stack
      gap={2}
      sx={{
        border: 1,
        borderColor: theme => theme.palette.darkerLightGrey.main,
        borderRadius: '10px',
        padding: '14px 16px',
        background: theme => theme.palette.lightGrey.main,
      }}>
      <Stack direction="row" alignItems="center" gap={1.75}>
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            flexShrink: 0,
            background: theme => theme.palette.primary.light,
            color: theme => theme.palette.primary.main,
            typography: 'h5',
            fontWeight: 700,
          }}>
          {initials || '?'}
        </Stack>
        <Stack gap={0.5} minWidth={0}>
          <Typography variant="h4" lineHeight={1}>
            {name}
          </Typography>
          {!!meta && (
            <Typography
              variant="caption"
              fontWeight={600}
              lineHeight={1}
              sx={{ color: 'text.secondary' }}>
              {meta}
            </Typography>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" gap={2} marginLeft="auto">
          {!disableCustomerEdit && (
            <Stack
              role="button"
              onClick={editing ? cancelEdit : openEdit}
              justifyContent="center"
              sx={{
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                color: theme =>
                  editing
                    ? theme.palette.outlineGrey.main
                    : theme.palette.primary.main,
                typography: 'caption',
                fontWeight: 600,
              }}>
              {editing ? 'Cancel' : 'Edit client'}
            </Stack>
          )}
          {canRemove && (
            <Stack
              role="button"
              onClick={onRemove}
              title="Remove client"
              alignItems="center"
              justifyContent="center"
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                cursor: 'pointer',
                flexShrink: 0,
                color: theme => theme.palette.outlineGrey.main,
                '&:hover': {
                  background: theme => theme.palette.darkerLightGrey.main,
                  color: theme => theme.palette.common.black,
                },
              }}>
              <CloseRounded sx={{ fontSize: 16 }} />
            </Stack>
          )}
        </Stack>
      </Stack>

      {editing && (
        <Stack
          gap={1.75}
          sx={{
            borderTop: 1,
            borderColor: theme => theme.palette.darkerLightGrey.main,
            paddingTop: 2,
          }}>
          <ClientFields
            namePrefix={namePrefix}
            disabled={disableCustomerEdit}
            disablePhone={!!read('customerGuestId')}
            hideBirthday={hideBirthday}
          />
          <Stack direction="row" alignItems="center" gap={1.25}>
            <Stack
              role="button"
              onClick={() => setEditing(false)}
              alignItems="center"
              justifyContent="center"
              sx={{
                ...pillSx,
                padding: '11px 24px',
                color: theme => theme.palette.common.white,
                background: theme => theme.palette.primary.main,
                '&:hover': { background: 'rgb(230, 40, 5)' },
              }}>
              Save
            </Stack>
            <Stack
              role="button"
              onClick={cancelEdit}
              alignItems="center"
              justifyContent="center"
              sx={{
                ...pillSx,
                padding: '10px 20px',
                color: 'text.secondary',
                background: theme => theme.palette.common.white,
                border: 1,
                borderColor: theme => theme.palette.darkerLightGrey.main,
                '&:hover': {
                  borderColor: theme => theme.palette.common.black,
                  color: theme => theme.palette.common.black,
                },
              }}>
              Cancel
            </Stack>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
